/**
 * Entity filters store using generic factory
 */

import type { BaseFilters } from '@/types/resource.types'

import type { RecordStateFilter } from '@scrambl/shared'
import { RecordStateFilterSchema } from '@scrambl/shared'

import { createFilterStore } from './factories'

export const normalizeEntityLifecycleStageFilter = (
  value: unknown,
): string[] => {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? [value]
      : []

  return Array.from(
    new Set(
      values
        .filter((stage): stage is string => typeof stage === 'string')
        .map(stage => stage.trim())
        .filter(Boolean),
    ),
  )
}

export interface EntityFilters extends BaseFilters {
  name?: string
  entityTypeId?: string
  includeCounts?: boolean
  includeCustomFields?: boolean
  /** Path C: lifecycle quick-filter from the chip row above the table. */
  lifecycleStage?: string[]
  // T2 record-state opt-in: unset = server default (active + provisional);
  // 'all' or an explicit state surfaces merged/archived tombstones (U2/U6).
  recordState?: RecordStateFilter
}

export const useEntityFiltersStore = createFilterStore<EntityFilters>({
  storageKey: 'entity-filters-storage',
  defaultFilters: {
    name: '',
    entityTypeId: '',
    lifecycleStage: [],
    page: 1,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  },
  autoResetPage: true,
  resetPageFields: [
    'name',
    'entityTypeId',
    'lifecycleStage',
    'recordState',
    'sortBy',
    'sortOrder',
  ],
  sanitizePersistedFilters: persisted => {
    const next = { ...persisted }
    delete next.status
    if (
      next.recordState !== undefined &&
      !RecordStateFilterSchema.safeParse(next.recordState).success
    ) {
      delete next.recordState
    }
    const persistedLifecycleStage = normalizeEntityLifecycleStageFilter(
      next.lifecycleStage,
    )
    next.lifecycleStage = persistedLifecycleStage

    if (
      next.columnFilters &&
      typeof next.columnFilters === 'object' &&
      !Array.isArray(next.columnFilters)
    ) {
      const columnFilters = {
        ...(next.columnFilters as Record<string, unknown>),
      }
      const columnLifecycleStage = normalizeEntityLifecycleStageFilter(
        columnFilters.lifecycleStage,
      )
      if (
        persistedLifecycleStage.length === 0 &&
        columnLifecycleStage.length > 0
      ) {
        next.lifecycleStage = columnLifecycleStage
      }
      delete columnFilters.lifecycleStage
      delete columnFilters.status
      next.columnFilters =
        Object.keys(columnFilters).length > 0 ? columnFilters : undefined
    }

    if (next.sortBy === 'status') {
      next.sortBy = 'name'
    }

    return next
  },
})
